import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Download, QrCode, RefreshCw } from 'lucide-react';
import {
  generateQRCodes,
  getQRCampaign,
  listQRCodes,
  listQRScans,
  type QRCodeRow,
  type QRCampaignDetail,
  type QRScanRow,
} from '@/lib/qr-api-client';

function toCsv(rows: Record<string, any>[], headers: string[]): string {
  const escape = (v: any) => {
    const s = v === null || v === undefined ? '' : String(v);
    if (s.includes('"') || s.includes(',') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push(headers.map((h) => escape(r[h])).join(','));
  }
  return lines.join('\n');
}

function downloadText(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function parseCsvSimple(text: string): Array<Record<string, string>> {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map((h) => h.trim());
  const out: Array<Record<string, string>> = [];

  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',');
    const row: Record<string, string> = {};
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = (cols[j] || '').trim();
    }
    out.push(row);
  }

  return out;
}

export default function QRCampaignDetailPage() {
  const { id } = useParams();
  const campaignId = String(id || '');

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<QRCampaignDetail | null>(null);
  const [codes, setCodes] = useState<QRCodeRow[]>([]);
  const [scans, setScans] = useState<QRScanRow[]>([]);

  const [quantity, setQuantity] = useState<number>(0);
  const [csvText, setCsvText] = useState<string>('');
  const [generating, setGenerating] = useState(false);

  const campaign = detail?.campaign;

  const reload = async () => {
    try {
      setLoading(true);
      const d = await getQRCampaign(campaignId);
      setDetail(d);

      const [c, s] = await Promise.all([
        listQRCodes(campaignId, 200),
        listQRScans(campaignId, 200),
      ]);
      setCodes(c);
      setScans(s);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!campaignId) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const responseRate = useMemo(() => {
    const scansUnique = detail?.counts.unique_scans || 0;
    const totalCodes = detail?.counts.codes || 0;
    if (!totalCodes) return 0;
    return (scansUnique / totalCodes) * 100;
  }, [detail]);

  const handleGenerateQuantity = async () => {
    if (!quantity || quantity <= 0) return toast.error('Quantity must be > 0');

    try {
      setGenerating(true);
      const created = await generateQRCodes(campaignId, { quantity });
      toast.success(`Generated ${created.length} QR code(s)`);
      setQuantity(0);
      await reload();

      const headers = ['short_code', 'tracking_url', 'qr_image_url'];
      downloadText(
        `qr_codes_${campaignId}_${new Date().toISOString().slice(0, 10)}.csv`,
        toCsv(created as any[], headers)
      );
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to generate codes');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateFromCsv = async () => {
    if (!csvText.trim()) return toast.error('Paste a CSV first');

    const rows = parseCsvSimple(csvText);
    if (!rows.length) return toast.error('CSV must include a header row + at least one data row');

    const recipients = rows.map((r) => ({
      customer_id: r.customer_id || undefined,
      destination_url: r.destination_url || undefined,
      metadata: {
        first_name: r.first_name,
        last_name: r.last_name,
        address: r.address,
        zip: r.zip,
      },
    }));

    try {
      setGenerating(true);
      const created = await generateQRCodes(campaignId, { recipients });
      toast.success(`Generated/upserted ${created.length} QR code(s)`);
      setCsvText('');
      await reload();

      const headers = ['customer_id', 'short_code', 'tracking_url', 'qr_image_url'];
      downloadText(
        `qr_codes_batch_${campaignId}_${new Date().toISOString().slice(0, 10)}.csv`,
        toCsv(created as any[], headers)
      );
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to generate batch codes');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading QR campaign...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="p-8">
        <div className="card">
          <p className="text-gray-700">Campaign not found.</p>
          <Link to="/admin/qr-tracking" className="btn-primary mt-4 inline-flex">
            Back
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/admin/qr-tracking" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
            <ArrowLeft size={16} /> Back to QR campaigns
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-2">{campaign.name}</h1>
          <p className="text-gray-600 mt-2 break-all">Destination: {campaign.destination_url}</p>
          {campaign.tracking_domain ? (
            <p className="text-gray-600 mt-1">Tracking domain: {campaign.tracking_domain}</p>
          ) : (
            <p className="text-yellow-800 bg-yellow-50 border border-yellow-100 rounded p-2 mt-2 text-sm">
              Tracking domain not set. Codes will default to the API host (OK for local testing, but set a branded domain for production).
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button onClick={reload} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2">
            <RefreshCw size={18} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card">
          <p className="text-sm font-medium text-gray-600">Codes</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{detail?.counts.codes || 0}</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-600">Scans</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{detail?.counts.scans || 0}</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-600">Unique Scans</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{detail?.counts.unique_scans || 0}</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-600">Response Rate</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{responseRate.toFixed(2)}%</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <QrCode className="text-gray-700" size={18} />
            <h2 className="text-lg font-semibold text-gray-900">Generate Codes</h2>
          </div>

          <div className="space-y-4">
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">Generate generic codes (by quantity)</h3>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  className="input w-40"
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                  min={0}
                />
                <button onClick={handleGenerateQuantity} disabled={generating} className="btn-primary flex items-center gap-2">
                  <QrCode size={18} /> Generate
                </button>
                <span className="text-xs text-gray-500">Auto-downloads a CSV after creation.</span>
              </div>
            </div>

            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">Batch generate from CSV</h3>
                <button
                  onClick={() => {
                    const sample =
                      'customer_id,first_name,last_name,address,zip\n' +
                      '12345,John,Smith,123 Main St,33647\n' +
                      '12346,Jane,Doe,456 Oak Ave,33629\n';
                    setCsvText(sample);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Insert sample
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                CSV columns supported: customer_id, first_name, last_name, address, zip, destination_url.
              </p>
              <textarea
                className="input w-full font-mono text-xs"
                rows={8}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder="Paste CSV here..."
              />
              <div className="flex items-center gap-3 mt-3">
                <button onClick={handleGenerateFromCsv} disabled={generating} className="btn-primary flex items-center gap-2">
                  <Download size={18} /> Generate + Download CSV
                </button>
                <span className="text-xs text-gray-500">
                  Codes are stable per (campaign, customer_id) because we upsert on that key.
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Scans</h2>
            <span className="text-xs text-gray-500">Showing {Math.min(scans.length, 200)} latest</span>
          </div>

          {scans.length === 0 ? (
            <p className="text-gray-600 text-sm">No scans recorded yet.</p>
          ) : (
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-4">When</th>
                    <th className="py-2 pr-4">Unique</th>
                    <th className="py-2 pr-4">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {scans.map((s) => (
                    <tr key={s.id} className="border-b last:border-b-0">
                      <td className="py-2 pr-4 whitespace-nowrap">{new Date(s.scanned_at).toLocaleString()}</td>
                      <td className="py-2 pr-4">{s.is_unique ? 'Yes' : 'No'}</td>
                      <td className="py-2 pr-4">
                        {[s.city, s.region, s.country].filter(Boolean).join(', ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card mt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Latest Codes</h2>
          <button
            onClick={() => {
              const headers = ['short_code', 'tracking_url', 'qr_image_url', 'customer_id'];
              downloadText(
                `qr_codes_${campaignId}_${new Date().toISOString().slice(0, 10)}.csv`,
                toCsv(codes as any[], headers)
              );
              toast.success('Downloaded codes CSV');
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            disabled={codes.length === 0}
          >
            <Download size={18} /> Export Codes
          </button>
        </div>

        {codes.length === 0 ? (
          <p className="text-gray-600 text-sm">No codes generated yet.</p>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Short Code</th>
                  <th className="py-2 pr-4">Tracking URL</th>
                  <th className="py-2 pr-4">QR Image</th>
                </tr>
              </thead>
              <tbody>
                {codes.slice(0, 50).map((c) => (
                  <tr key={c.id} className="border-b last:border-b-0">
                    <td className="py-2 pr-4 font-mono">{c.short_code}</td>
                    <td className="py-2 pr-4">
                      <a className="text-blue-600 hover:text-blue-800 break-all" href={c.tracking_url} target="_blank" rel="noreferrer">
                        {c.tracking_url}
                      </a>
                    </td>
                    <td className="py-2 pr-4">
                      <a className="text-blue-600 hover:text-blue-800 break-all" href={c.qr_image_url} target="_blank" rel="noreferrer">
                        {c.qr_image_url}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {codes.length > 50 ? (
              <p className="text-xs text-gray-500 mt-2">Showing first 50 of {codes.length} codes</p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
